import type { ContractRouterClient } from "@orpc/contract"
import type {
	adminContract,
	liveEventContract,
	negotiationContract,
	orderContract,
} from "@repo/api-dtos"
import type { gameAdminContract } from "@repo/game-admin"
import type { marketplaceAdminContract } from "@repo/marketplace-admin"

// ============================================================================
// TYPES
// ============================================================================

/**
 * Type for the game-admin oRPC client
 */
export type GameAdminOrpcClient = ContractRouterClient<typeof gameAdminContract>

/**
 * Type for the marketplace-admin oRPC client
 */
export type MarketplaceAdminOrpcClient = ContractRouterClient<
	typeof marketplaceAdminContract
>

/**
 * Type for the main oRPC client (subset used by admin)
 */
export type MainOrpcClient = ContractRouterClient<{
	admin: typeof adminContract
	order: typeof orderContract
	negotiation: typeof negotiationContract
	liveEvent: typeof liveEventContract
}>

// ============================================================================
// HELPERS
// ============================================================================

const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1"])

/**
 * The backend base URL. When the admin is opened from another host than
 * localhost (a tunnel to port 3001) while the backend is local, calls go to
 * the page's own origin, which the Vite dev or preview server proxies to the
 * backend. A deployed admin never points at a localhost backend.
 */
export function getApiUrl(): string {
	const configured = import.meta.env.VITE_API_URL || "http://localhost:3000"
	if (
		typeof window !== "undefined" &&
		LOCAL_HOSTNAMES.has(new URL(configured).hostname) &&
		!LOCAL_HOSTNAMES.has(window.location.hostname)
	) {
		return window.location.origin
	}
	return configured
}

export function getOrpcUrl(): string {
	return `${getApiUrl()}/orpc/game-admin`
}

export function getMarketplaceOrpcUrl(): string {
	return `${getApiUrl()}/orpc/marketplace-admin`
}

export function getMainOrpcUrl(): string {
	return `${getApiUrl()}/orpc`
}

export function getGameAdminWsUrl(): string {
	return `${getApiUrl().replace(/^http/, "ws").replace(/\/$/, "")}/ws/game-admin`
}

export function getMainWsUrl(): string {
	return `${getApiUrl().replace(/^http/, "ws").replace(/\/$/, "")}/ws`
}
